import { CosmosClient } from '@azure/cosmos';

const endpoint = process.env.REACT_APP_COSMOS_DB_URI;
const key = process.env.REACT_APP_COSMOS_DB_KEY;
const databaseId = process.env.REACT_APP_COSMOS_DB_ID;
const containerId = 'financialcontrollers';

const FINANCIAL_CONTROLLER_ROLE = 'Financial Controller';
const COMPLIANCE_CHECK_ROLE = 'Compliance Check';
const FIRST_APPROVER_ROLE = 'First Approver';
const TREASURY_EMAIL_ROLE = 'Treasury Email';
const ACCOUNTING_EMAIL_ROLE = 'Accounting Email';

const FINANCIAL_CONTROLLER_ROLES = [
    FINANCIAL_CONTROLLER_ROLE,
    COMPLIANCE_CHECK_ROLE,
    TREASURY_EMAIL_ROLE,
    ACCOUNTING_EMAIL_ROLE,
    FIRST_APPROVER_ROLE,
];

const SINGLE_ASSIGNEE_ROLES = new Set([
    FINANCIAL_CONTROLLER_ROLE,
    COMPLIANCE_CHECK_ROLE,
]);

const client = new CosmosClient({ endpoint, key });
const database = client.database(databaseId);
const container = database.container(containerId);

const normalizeEmail = (email) => (email || '').trim().toLowerCase();

const normalizeFinancialController = (assignment) => ({
    ...assignment,
    name: assignment.name || assignment.controller,
    controller: assignment.controller || assignment.name,
    email: assignment.email || assignment.username,
});

const createFinancialControllerError = (message) => {
    const error = new Error(message);
    error.code = 'FINANCIAL_CONTROLLER_VALIDATION';
    return error;
};

const getFinancialControllers = async () => {
    try {
        const { resources } = await container.items.readAll().fetchAll();
        return resources
            .map(normalizeFinancialController)
            .sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
        console.error('Error getting financial controllers:', error);
        throw error;
    }
};

const getFinancialControllerByRole = async (role) => {
    const assignments = await getFinancialControllers();
    return assignments.find((assignment) => assignment.role === role) || null;
};

const insertFinancialController = async (user, role) => {
    try {
        if (!user) {
            throw createFinancialControllerError('Please select a user');
        }

        if (!FINANCIAL_CONTROLLER_ROLES.includes(role)) {
            throw createFinancialControllerError('Please select a valid role');
        }

        const email = user.email || user.username;
        const normalizedEmail = normalizeEmail(email);

        if (!user.id || !user.name || !normalizedEmail) {
            throw createFinancialControllerError('Selected user is missing name or email');
        }

        const assignments = await getFinancialControllers();
        const assignedUserRoles = assignments.filter((assignment) => (
            assignment.userId === user.id ||
            normalizeEmail(assignment.email) === normalizedEmail
        ));

        const duplicateUserRole = assignedUserRoles.find((assignment) => assignment.role === role);

        if (duplicateUserRole) {
            throw createFinancialControllerError(`${user.name} already has the ${duplicateUserRole.role} role`);
        }

        const lockedUserRole = assignedUserRoles.find((assignment) => (
            SINGLE_ASSIGNEE_ROLES.has(role) ||
            SINGLE_ASSIGNEE_ROLES.has(assignment.role)
        ));

        if (lockedUserRole) {
            throw createFinancialControllerError(`${user.name} cannot be assigned to ${role} because they already have the ${lockedUserRole.role} role`);
        }

        const assignedRole = assignments.find((assignment) => (
            SINGLE_ASSIGNEE_ROLES.has(role) &&
            assignment.role === role
        ));

        if (assignedRole) {
            throw createFinancialControllerError(`${role} is already assigned to ${assignedRole.name}`);
        }

        const now = new Date().toISOString();
        const { resource } = await container.items.create({
            userId: user.id,
            name: user.name,
            controller: user.name,
            email,
            role,
            createdAt: now,
            updatedAt: now,
        });

        return normalizeFinancialController(resource);
    } catch (error) {
        console.error('Error inserting financial controller:', error);
        throw error;
    }
};

const deleteFinancialController = async (id) => {
    try {
        await container.item(id, id).delete();
    } catch (error) {
        console.error('Error deleting financial controller:', error);
        throw error;
    }
};

export {
    ACCOUNTING_EMAIL_ROLE,
    COMPLIANCE_CHECK_ROLE,
    FINANCIAL_CONTROLLER_ROLE,
    FINANCIAL_CONTROLLER_ROLES,
    FIRST_APPROVER_ROLE,
    TREASURY_EMAIL_ROLE,
    deleteFinancialController,
    getFinancialControllerByRole,
    getFinancialControllers,
    insertFinancialController,
};
